import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { KioskIdleService } from '@features/kiosk/kiosk-idle.service';

/**
 * Экран ожидания АРМ: что видно, когда за рабочим местом никого нет.
 *
 * Героем сделаны часы, потому что в реанимации часы — самый запрашиваемый объект в
 * помещении, и простаивающий у койки АРМ честнее всего работает как настенные часы.
 * Всё остальное на экране приглушено до подписи.
 *
 * Условия показа и связь с сессией — в {@link KioskIdleService}; здесь только отрисовка.
 */
@Component({
  selector: 'rais-kiosk-idle-screen',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (idle.isVisible$()) {
      <div class="screen" role="status" (pointerdown)="idle.dismiss()">
        <img class="mark" src="assets/img/app_logo.svg" alt="" width="132" height="48" />
        <p class="time">{{ time$() }}</p>
        <p class="date">{{ date$() }}</p>
        <p class="hint">Коснитесь экрана или нажмите любую клавишу</p>
      </div>
    }
  `,
  styles: [
    `
      @import 'colors';
      @import 'scales';

      .screen {
        position: fixed;
        inset: 0;
        // Выше модального окна входа (1000): оно открыто ровно в том состоянии, когда экран
        // ожидания имеет право появиться, и первое касание должно попасть по заставке, а не по
        // форме. 1050 — уже принятая в проекте ступень над модалкой.
        z-index: 1050;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: @rais--neutral-50;
        animation: rais-kiosk-idle-in 0.4s ease-out;
      }

      .mark {
        width: 132px;
        height: 48px;
        opacity: 0.9;
      }

      .time {
        margin: @rais--spacing-32 0 0;
        color: @rais--neutral-800;
        // Кегля такого порядка в шкале §3 нет и быть не может: это не текст интерфейса, а
        // единственный объект на пустом экране. Растёт с экраном АРМ, упирается в 152px.
        font-size: clamp(72px, 13vw, 152px);
        font-variant-numeric: tabular-nums;
        line-height: 1;
        letter-spacing: -0.02em;
      }

      .date {
        margin: @rais--spacing-16 0 0;
        color: @rais--neutral-600;
        font-size: @rais--font-size-20;

        &::first-letter {
          text-transform: uppercase;
        }
      }

      .hint {
        position: absolute;
        bottom: @rais--spacing-48;
        margin: 0;
        color: @rais--neutral-600;
        font-size: @rais--font-size-14;
      }

      @keyframes rais-kiosk-idle-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .screen {
          animation: none;
        }
      }
    `,
  ],
})
export class KioskIdleScreenComponent {
  protected idle = inject(KioskIdleService);

  private now$ = signal(new Date());

  protected time$ = computed(() => format(this.now$(), 'HH:mm'));
  protected date$ = computed(() => format(this.now$(), 'EEEE, d MMMM', { locale: ru }));

  constructor() {
    // Часы идут только пока экран на виду: в остальное время АРМ занят работой.
    effect((onCleanup) => {
      if (!this.idle.isVisible$()) return;

      this.now$.set(new Date());
      const ticker = setInterval(() => this.now$.set(new Date()), 1000);
      onCleanup(() => clearInterval(ticker));
    });
  }
}
